import { useEffect, useState } from 'react';
import {
  Alert, Box, Checkbox, Chip, CircularProgress, FormControlLabel, Radio, RadioGroup, Stack, Typography,
} from '@mui/material';
import { CTWA_PLACEMENTS } from '../../../config/urls';
import { ColorsConstants } from '../../../cssUtils/constants';
import { useCtwaApi } from './api';
import { GOALS, SPECIAL_AD_CATEGORIES } from './constants';
import { isLockedCategory } from './validation';
import { ChoiceCard, Note } from './ui';

const radioSx = { color: ColorsConstants.primaryBlue100, '&.Mui-checked': { color: ColorsConstants.primaryBlue100 } };

export const ObjectiveSection = ({ goal, specialAdCategory, onChange }) => (
  <Box>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
      {GOALS.map((g) => (
        <ChoiceCard key={g.value} selected={goal === g.value} onClick={() => onChange({ goal: g.value })} title={g.label} desc={g.desc} />
      ))}
    </Stack>
    <Note sx={{ mt: 1 }}>Destination, optimization goal and bidding are set automatically for you.</Note>

    <Typography variant="body2" fontWeight={700} sx={{ mt: 3, mb: 1 }}>Is this ad about any of these?</Typography>
    <RadioGroup value={specialAdCategory} onChange={(e) => onChange({ specialAdCategory: e.target.value })}>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {SPECIAL_AD_CATEGORIES.map((c) => (
          <FormControlLabel
            key={c.value} value={c.value} control={<Radio size="small" sx={radioSx} />}
            label={<Typography variant="body2">{c.label}</Typography>}
            sx={{
              m: 0, pr: 1.5, borderRadius: 999,
              border: `${specialAdCategory === c.value ? 2 : 1}px solid ${specialAdCategory === c.value ? ColorsConstants.primaryBlue100 : ColorsConstants.primaryGray20}`,
            }}
          />
        ))}
      </Stack>
    </RadioGroup>
    <Note sx={{ mt: 1.5, p: 1.25, borderRadius: 1, backgroundColor: '#F9FAFB' }}>
      Meta requires this. If you pick a category, some targeting options are limited
      {isLockedCategory(specialAdCategory) ? ': age is fixed at 18–65+, gender is All and pincodes can\'t be used.' : '.'}
    </Note>
  </Box>
);

// Manual placements come from the backend config, so the list always matches what Meta currently supports.
export const PlacementsSection = ({ mode, selected, igConnected, onChange }) => {
  const api = useCtwaApi();
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (mode !== 'manual' || options) return;
    api.get(CTWA_PLACEMENTS)
      .then((d) => setOptions(d.placements || []))
      .catch((e) => { setError(e.message); setOptions([]); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const toggle = (key) => onChange({
    placements: selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key],
  });

  return (
    <Box>
      <RadioGroup value={mode} onChange={(e) => onChange({ placementMode: e.target.value })}>
        <FormControlLabel
          value="automatic" control={<Radio size="small" sx={radioSx} />}
          label={<Box><Typography variant="body2" fontWeight={600}>Automatic (recommended)</Typography>
            <Note>Meta shows your ad where it performs best across Facebook{igConnected ? ' and Instagram' : ''}.</Note></Box>}
        />
        <FormControlLabel
          value="manual" control={<Radio size="small" sx={radioSx} />}
          label={<Box><Typography variant="body2" fontWeight={600}>Manual placements</Typography>
            <Note>Choose specific placements.</Note></Box>}
        />
      </RadioGroup>

      {mode === 'manual' && (
        <Box sx={{ mt: 1.5, pl: 4 }}>
          {!options && <CircularProgress size={18} />}
          {error && <Alert severity="error">Couldn't load placements: {error}</Alert>}
          {options?.map((p) => {
            const blocked = p.requires_instagram && !igConnected;
            return (
              <FormControlLabel
                key={p.key}
                control={<Checkbox size="small" sx={radioSx} checked={selected.includes(p.key)} disabled={blocked} onChange={() => toggle(p.key)} />}
                label={
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="body2">{p.label}</Typography>
                    {blocked && <Chip size="small" label="Link Instagram first" sx={{ height: 18, fontSize: 10 }} />}
                  </Stack>
                }
                sx={{ display: 'flex' }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};
